/**
 * TX9 — formatters y labels del módulo Tesorería.
 * Un solo origen; no mostrar enums técnicos en UI.
 */

export function formatMoney(n: number | null | undefined, currency = 'ARS'): string {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('es-AR', { style: 'currency', currency });
}

/** Fecha corta: 05/09/2026 */
export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' || typeof iso === 'number' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Hora: 10:32 */
export function formatTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' || typeof iso === 'number' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

/** Fecha + hora: 05/09/2026 10:32 */
export function formatDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' || typeof iso === 'number' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(d)} ${formatTime(d)}`;
}

const REGISTER_TYPE: Record<string, string> = {
  CENTRAL_TREASURY: 'Tesorería Central',
  BRANCH_MASTER: 'Caja Madre',
  OPERATIVE: 'Caja Operativa',
};

export function registerTypeLabel(type?: string | null): string {
  if (!type) return '—';
  return REGISTER_TYPE[type] ?? type;
}

const SESSION_STATUS: Record<string, string> = {
  OPEN: 'Abierta',
  CLOSED: 'Cerrada',
  AUDITED: 'Arqueada',
};

export function sessionStatusLabel(status?: string | null): string {
  if (!status) return '—';
  return SESSION_STATUS[status] ?? status;
}

const TRANSFER_STATUS: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  COMPLETED: 'Completada',
  CANCELLED: 'Cancelada',
};

export function transferStatusLabel(status?: string | null): string {
  if (!status) return '—';
  return TRANSFER_STATUS[status] ?? status;
}

export function transferStatusTone(status?: string | null): string {
  switch (status) {
    case 'PENDING':
      return 'text-amber-800';
    case 'APPROVED':
    case 'COMPLETED':
      return 'text-green-700';
    case 'REJECTED':
    case 'CANCELLED':
      return 'text-red-700';
    default:
      return 'text-slate-700';
  }
}

const DIRECTION: Record<string, string> = {
  DEPOSIT: 'Depósito',
  WITHDRAWAL: 'Extracción',
  IN: 'Sobrante',
  OUT: 'Faltante',
};

export function directionLabel(direction?: string | null): string {
  if (!direction) return '—';
  return DIRECTION[direction] ?? direction;
}

const ADJUSTMENT_STATUS: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobado',
  COMPLETED: 'Aprobado',
  REJECTED: 'Rechazado',
};

export function adjustmentStatusLabel(status?: string | null): string {
  if (!status) return '—';
  return ADJUSTMENT_STATUS[status] ?? transferStatusLabel(status);
}

/** CTA primario estándar Tesorería (no mezclar con blue-600). */
export const CTA_PRIMARY =
  'inline-flex items-center justify-center px-3 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50';

export const CTA_SECONDARY =
  'inline-flex items-center justify-center px-3 py-2 text-sm rounded-md border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-50';

export const CTA_DANGER =
  'inline-flex items-center justify-center px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50';

/**
 * Convierte errores de API en mensaje usable.
 * Nunca devolver stack/JSON crudo.
 */
export function humanizeTreasuryError(raw: unknown, fallback = 'No pudimos completar la operación.'): string {
  if (raw == null || raw === '') return fallback;
  let msg = '';
  if (typeof raw === 'string') msg = raw;
  else if (Array.isArray(raw)) msg = raw.map(String).join(' ');
  else if (typeof raw === 'object' && raw !== null && 'message' in raw) {
    const m = (raw as { message: unknown }).message;
    msg = Array.isArray(m) ? m.join(' ') : String(m ?? '');
  } else msg = String(raw);

  msg = msg.trim();
  if (!msg) return fallback;

  const lower = msg.toLowerCase();
  if (lower.includes('sesión') && (lower.includes('cerr') || lower.includes('open'))) {
    return 'La sesión de caja cambió. Actualizá e intentá de nuevo.';
  }
  if (lower.includes('fondos insuficientes') || lower.includes('saldo insuficiente')) {
    return 'No hay saldo suficiente en el origen para esta operación.';
  }
  if (lower.includes('permiso') || lower.includes('forbidden') || lower.includes('denegad')) {
    return 'No tenés permiso para esta acción.';
  }
  if (lower.includes('razón social') || lower.includes('legalentity') || lower.includes('legal entity')) {
    return 'La caja y la cuenta deben ser de la misma empresa.';
  }
  if (lower.includes('vincul')) {
    return 'Falta el vínculo entre la caja y la cuenta bancaria.';
  }
  // Evitar dumps técnicos
  if (msg.startsWith('{') || msg.includes('Exception') || msg.includes('ECONNREFUSED')) {
    return fallback;
  }
  return msg;
}
